"use client"

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { X } from "lucide-react"

/**
 * Full-screen portfolio viewer.
 *
 * Paging is a scroll-snap rail, not a transform the component animates itself:
 * the browser gives us momentum, rubber-banding and trackpad support for free,
 * and it is the same mechanism the social embed rail already uses. Arrow keys
 * scroll that rail rather than driving separate state, so there is one source
 * of truth for which image is showing.
 */
export type LightboxImage = { id: string; image_url: string; caption?: string | null }

const HINT_KEY = "booth.portfolio-swipe-hint.v1"

function hintAlreadySeen(): boolean {
  // Private windows and "block site data" throw on access rather than
  // returning null, and a viewer who can't be remembered should still get the
  // image — so a failed read means "show it once this session", never a crash.
  try {
    return window.localStorage.getItem(HINT_KEY) === "1"
  } catch {
    return false
  }
}

function rememberHint() {
  try {
    window.localStorage.setItem(HINT_KEY, "1")
  } catch {
    /* storage unavailable — the hint simply shows again next time */
  }
}

export function PortfolioLightbox({
  images,
  startIndex,
  onClose,
}: {
  images: LightboxImage[]
  startIndex: number
  onClose: () => void
}) {
  const railRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const [index, setIndex] = useState(startIndex)
  const [showHint, setShowHint] = useState(false)

  const step = useCallback(() => railRef.current?.clientWidth ?? 0, [])

  // Land on the thumbnail that was clicked before the first paint, so the
  // viewer never flashes image 1 and then jumps.
  useLayoutEffect(() => {
    const el = railRef.current
    if (!el) return
    el.scrollTo({ left: startIndex * el.clientWidth, behavior: "instant" as ScrollBehavior })
  }, [startIndex])

  const goTo = useCallback(
    (i: number) => {
      const el = railRef.current
      const width = step()
      if (!el || width <= 0) return
      const clamped = Math.min(images.length - 1, Math.max(0, i))
      el.scrollTo({ left: clamped * width, behavior: "smooth" })
    },
    [images.length, step],
  )

  const onScroll = useCallback(() => {
    const el = railRef.current
    const width = step()
    if (!el || width <= 0) return
    const next = Math.min(images.length - 1, Math.max(0, Math.round(el.scrollLeft / width)))
    setIndex(prev => (prev === next ? prev : next))
  }, [images.length, step])

  // The hint is a nudge, not a tutorial: one image means nothing to swipe to,
  // and once someone has seen it they never see it again on this device.
  useEffect(() => {
    if (images.length < 2 || hintAlreadySeen()) return
    setShowHint(true)
    rememberHint()
    const t = setTimeout(() => setShowHint(false), 2600)
    return () => clearTimeout(t)
  }, [images.length])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowRight") goTo(index + 1)
      else if (e.key === "ArrowLeft") goTo(index - 1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [goTo, index, onClose])

  // Hold the page still underneath, and restore exactly what was there before
  // (the profile does not set overflow itself, but a parent might later).
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    closeRef.current?.focus()
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Portfolio"
      /* Above the floating launcher (z-50): it is also fixed and later in the
         DOM, so at an equal z it painted over the image and through the hint. */
      className="fixed inset-0 z-[60] bg-black/95 animate-in fade-in duration-200"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        style={{ top: "calc(1rem + env(safe-area-inset-top))" }}
      >
        <X className="h-5 w-5" strokeWidth={1.75} />
      </button>

      <div
        ref={railRef}
        onScroll={onScroll}
        className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map(img => (
          <div
            key={img.id}
            className="flex h-full w-full shrink-0 snap-center snap-always cursor-zoom-out items-center justify-center p-4 sm:p-8"
            onClick={onClose}
          >
            {/* Natural aspect ratio, no card frame and no letterbox — the point
                of opening an image is seeing it as it was shot. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={img.image_url}
              alt={img.caption || ""}
              className="h-auto max-h-full w-auto max-w-full rounded-lg object-contain"
              onClick={e => e.stopPropagation()}
            />
          </div>
        ))}
      </div>

      {images.length > 1 && (
        <div
          className={`pointer-events-none absolute inset-x-0 flex justify-center transition-opacity duration-500 ${
            showHint ? "opacity-100" : "opacity-0"
          }`}
          style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
          aria-hidden
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 booth-swipe-nudge" aria-hidden>
              <path
                d="M9 6 4.5 12 9 18M15 6l4.5 6-4.5 6"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Swipe for more
          </span>
        </div>
      )}
    </div>
  )
}
